<template>
  <div class="project-details">
    <b-loading :active="isLoading" :is-full-page="false"></b-loading>

    <OmxPageHeader
        :title="headerTitle"
        :subtitle="headerSubtitle"
        :pills="headerPills"
        :avatar="projectPicUrl"
        :avatar-fallback="headerInitials"
        sticky
    >
      <template #actions>
        <template v-if="isEditMode">
          <b-button @click="cancelClicked" icon-left="close-thick" tabindex="11">
            {{ $t('common.cancel') }}
          </b-button>
          <OmxPermissionButton
              :allowed="canSave"
              type="is-primary"
              @click="saveClicked"
              icon-left="content-save"
              tabindex="12"
          >{{ $t('common.save') }}</OmxPermissionButton>
          <b-button
              v-if="canArchive"
              type="is-danger"
              icon-left="archive-arrow-down"
              @click="archiveClicked"
          >{{ $t('project-details-view.archive-project-button') }}</b-button>
          <b-button
              v-else-if="canUnarchive"
              icon-left="archive-arrow-up-outline"
              @click="unArchiveClicked"
          >{{ $t('project-details-view.unarchive-project-button') }}</b-button>
        </template>
        <template v-else>
          <b-button @click="backClicked" icon-left="arrow-left">
            {{ $t('common.back') }}
          </b-button>
          <OmxPermissionButton
              :allowed="canEdit"
              type="is-primary"
              icon-left="pencil"
              @click="editClicked"
          >{{ $t('common.edit') }}</OmxPermissionButton>
          <b-dropdown :triggers="['hover']" aria-role="list" position="is-bottom-left">
            <template #trigger>
              <b-button icon-left="dots-horizontal" />
            </template>
            <b-dropdown-item
                v-if="!isTemplateMode && currentUserAccess('ProjectLogs', 'VIEW', id)"
                aria-role="listitem" @click="worklogClicked">
              <b-icon icon="eye" size="is-small" /> {{ $t('projects-view.enter-worklog') }}
            </b-dropdown-item>
            <b-dropdown-item
                v-if="!isTemplateMode && currentUserAccess('ProjectSummary', 'VIEW', id)"
                aria-role="listitem" @click="summaryClicked">
              <b-icon icon="chart-gantt" size="is-small" /> {{ $t('projects-view.show-summary') }}
            </b-dropdown-item>
            <b-dropdown-item
                v-if="!isTemplateMode && currentUserAccess('ProjectDetails', 'VIEW', id)"
                aria-role="listitem" @click="exportClicked">
              <b-icon icon="file-download" size="is-small" /> {{ $t('projects-view.export') }}
            </b-dropdown-item>
            <b-dropdown-item
                v-if="!isTemplateMode && currentUserAccess('ProjectTemplates', 'UPDATE')"
                aria-role="listitem" @click="saveAsTemplateClicked">
              <b-icon icon="content-duplicate" size="is-small" /> {{ $t('projects-view.save-as-template') }}
            </b-dropdown-item>
            <b-dropdown-item
                v-if="isTemplateMode && currentUserAccess('ProjectDetails', 'CREATE')"
                aria-role="listitem" @click="saveAsProjectClicked">
              <b-icon icon="android-studio" size="is-small" /> {{ $t('projects-view.create-project-from-template') }}
            </b-dropdown-item>
            <hr class="dropdown-divider" v-if="canDelete">
            <b-dropdown-item
                v-if="canDelete"
                aria-role="listitem" @click="deleteClicked">
              <b-icon icon="delete" size="is-small" type="is-danger" /> {{ $t('common.delete') }}
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </template>
    </OmxPageHeader>

    <b-message v-if="isTemplateMode" type="is-info" has-icon class="project-details__template-msg">
      {{ $t('project-details-view.seeing-template-notice') }}
    </b-message>

    <div
        v-if="showStats"
        class="project-details__stats"
    >
      <OmxStatTile
          icon="clock-outline"
          :label="$t('project-details-view.stat-hours-label')"
      >
        <template v-if="isLoadingStats"><OmxSkeleton variant="block" width="80px" height="22px" /></template>
        <template v-else>{{ statHoursValue }}</template>
        <template #delta v-if="!isLoadingStats && statHoursDelta">{{ statHoursDelta }}</template>
      </OmxStatTile>

      <OmxStatTile
          icon="currency-eur"
          :tone="statProfitTone"
          :delta-direction="statProfitDirection"
          :label="$t('project-details-view.stat-profit-label')"
      >
        <template v-if="isLoadingStats"><OmxSkeleton variant="block" width="80px" height="22px" /></template>
        <template v-else>{{ statProfitValue }}</template>
      </OmxStatTile>

      <OmxStatTile
          icon="account-multiple"
          tone="warm"
          delta-direction="flat"
          :label="$t('project-details-view.stat-external-label')"
      >
        <template v-if="isLoadingStats"><OmxSkeleton variant="block" width="80px" height="22px" /></template>
        <template v-else>{{ statExternalValue }}</template>
        <template #delta v-if="!isLoadingStats && statExternalDelta">{{ statExternalDelta }}</template>
      </OmxStatTile>

      <OmxStatTile
          icon="flag-outline"
          tone="purple"
          delta-direction="flat"
          :label="$t('project-details-view.stat-tasks-label')"
      >
        <template v-if="isLoadingTasks"><OmxSkeleton variant="block" width="80px" height="22px" /></template>
        <template v-else>{{ statTasksValue }}</template>
      </OmxStatTile>
    </div>

    <OmxCard :title="$t('project-details-view.section-general')" class="project-details__card">
      <div class="columns">
        <div class="column is-half">
                  <OmxField
                      v-if="isEditMode"
                      v-model="projectData.code"
                      :label="$t('project-details-view.project-code-field')"
                      :placeholder="$t('project-details-view.project-code-field')"
                      tabindex="1"
                  />
                  <OmxField
                      v-else
                      :label="$t('project-details-view.project-code-field')"
                      :value="projectData.code"
                      view-only
                  />
                  <OmxField
                      v-model="projectData.title"
                      :label="$t('project-details-view.project-title-field')"
                      :placeholder="$t('project-details-view.project-title-field')"
                      :view-only="!isEditMode"
                      tabindex="2"
                  />
                  <OmxTagInput
                      v-if="!isTemplateMode"
                      v-model="projectManagersTags"
                      :label="$t('project-details-view.project-manager-field')"
                      :data="filteredProjectManagers"
                      :loading="isFetching"
                      :view-only="!isEditMode || !currentUserAccess('Users', UserActions.UPDATE)"
                      field="email"
                      icon="account-box"
                      :placeholder="$t('project-details-view.project-manager-placeholder')"
                      tabindex="3"
                      @typing="getFilteredProjectManagers"
                      @input="onProjectManagersInput"
                  >
                    <template #tag="{ tag }">{{ getFullName(tag.name) }}</template>
                  </OmxTagInput>
                  <OmxTagInput
                      v-if="!isTemplateMode"
                      v-model="siteManagersTags"
                      :label="$t('project-details-view.site-managers-field')"
                      :data="filteredSiteManagers"
                      :loading="isFetching"
                      :view-only="!isEditMode"
                      field="email"
                      icon="account-box"
                      :placeholder="$t('project-details-view.site-managers-placeholder')"
                      tabindex="4"
                      @typing="getFilteredSiteManagers"
                      @input="onSiteManagersInput"
                  >
                    <template #tag="{ tag }">{{ getFullName(tag.name) }}</template>
                  </OmxTagInput>
                  <OmxField
                      v-model="projectData.description"
                      type="textarea"
                      :label="$t('project-details-view.project-description-field')"
                      :placeholder="$t('project-details-view.project-description-field')"
                      :view-only="!isEditMode"
                      tabindex="5"
                  />
                </div>
                <div class="column is-half">
                  <OmxField
                      v-if="!isTemplateMode && isEditMode"
                      v-model="projectData.phase"
                      type="select"
                      :label="$t('project-details-view.project-status-field')"
                      :placeholder="$t('project-details-view.project-status-placeholder')"
                      :options="projectPhaseFieldOptions"
                      tabindex="6"
                  />
                  <OmxField
                      v-else-if="!isTemplateMode"
                      :label="$t('project-details-view.project-status-field')"
                      view-only
                  ><template #value>{{ projectStatus }}</template></OmxField>
                  <div class="notification pt-5 mt-5">
                    <div class="columns" v-if="isEditMode && !isAdvancedEditMode && !isTemplateMode && !isInNewMode">
                      <div class="column is-half">
                        <b-button class="button is-dark" @click="advancedEditClicked" tabindex="12" icon-left="pencil">
                          {{ $t('project-details-view.advanced-edit-details-button') }}
                        </b-button>
                      </div>
                    </div>
                    <div class="columns">
                      <div class="column is-half">
                        <OmxField
                            v-if="isEditMode && (isAdvancedEditMode || isInNewMode || isTemplateMode)"
                            v-model="projectData.internal_personal_cost"
                            type="number"
                            min="0"
                            step="1"
                            :label="$t('project-details-view.internal-personal-cost-field')"
                            :placeholder="$t('project-details-view.internal-personal-cost-field')"
                            :hint="$t('project-details-view.internal-personal-cost-tooltip')"
                            tabindex="7"
                        />
                        <OmxField
                            v-else
                            :label="$t('project-details-view.internal-personal-cost-field')"
                            :hint="$t('project-details-view.internal-personal-cost-tooltip')"
                            view-only
                        ><template #value>{{ getFormattedPricePerDuration(projectData.internal_personal_cost, "hour") }}</template></OmxField>
                      </div>
                      <div class="column is-half">
                        <OmxField
                            v-if="isEditMode && (isAdvancedEditMode || isInNewMode || isTemplateMode)"
                            v-model="projectData.internal_personal_day_in_hours"
                            type="number"
                            min="0"
                            max="24"
                            step="0.5"
                            :label="$t('project-details-view.internal-personal-hours-field')"
                            :placeholder="$t('project-details-view.internal-personal-hours-field')"
                            :hint="$t('project-details-view.internal-personal-hours-tooltip')"
                            tabindex="8"
                        />
                        <OmxField
                            v-else
                            :label="$t('project-details-view.internal-personal-hours-field')"
                            :hint="$t('project-details-view.internal-personal-hours-tooltip')"
                            view-only
                        ><template #value>{{ getFormattedTimeHour(projectData.internal_personal_day_in_hours) }}</template></OmxField>
                      </div>
                    </div>
                    <div class="columns">
                      <div class="column is-half">
                        <OmxField
                            v-if="isEditMode && (isAdvancedEditMode || isInNewMode || isTemplateMode)"
                            v-model="projectData.external_personal_cost"
                            type="number"
                            min="0"
                            step="1"
                            :label="$t('project-details-view.external-personal-cost-field')"
                            :placeholder="$t('project-details-view.external-personal-cost-field')"
                            :hint="$t('project-details-view.external-personal-cost-tooltip')"
                            tabindex="9"
                        />
                        <OmxField
                            v-else
                            :label="$t('project-details-view.external-personal-cost-field')"
                            :hint="$t('project-details-view.external-personal-cost-tooltip')"
                            view-only
                        ><template #value>{{ getFormattedPricePerDuration(projectData.external_personal_cost, "hour") }}</template></OmxField>
                      </div>
                      <div class="column is-half">
                        <OmxField
                            v-if="isEditMode && (isAdvancedEditMode || isInNewMode || isTemplateMode)"
                            v-model="projectData.external_personal_day_in_hours"
                            type="number"
                            min="0"
                            max="24"
                            step="0.5"
                            :label="$t('project-details-view.external-personal-hours-field')"
                            :placeholder="$t('project-details-view.external-personal-hours-field')"
                            :hint="$t('project-details-view.external-personal-hours-tooltip')"
                            tabindex="10"
                        />
                        <OmxField
                            v-else
                            :label="$t('project-details-view.external-personal-hours-field')"
                            :hint="$t('project-details-view.external-personal-hours-tooltip')"
                            view-only
                        ><template #value>{{ getFormattedTimeHour(projectData.external_personal_day_in_hours) }}</template></OmxField>
                      </div>
                    </div>
                  </div>
                  <ImagePicker
                      v-if="isEditMode"
                      :is-edit-mode="isEditMode"
                      :image-url="projectPicUrl"
                      @onImageFileRemoveSelected="onProjectPicFileRemoveSelected"
                      @onImageFileSelected="onProjectPicFileSelected"
                  />
                </div>
              </div>
    </OmxCard>

    <OmxCard
        v-if="!isInNewMode"
        density="compact"
        class="project-details__tabs-card"
    >
      <b-tabs type="is-boxed" v-model="activeTab" class="project-details__tabs">
        <b-tab-item :label="$t('tasks-view.component-title')" icon="format-list-checks">
          <Tasks :project_id="id" :is_template="isTemplateMode" @tasks-loaded="onTasksLoaded" />
        </b-tab-item>
        <b-tab-item :label="$t('personnel-view.component-title')" icon="account-hard-hat">
          <Personnel :project_id="id" :is_template="isTemplateMode" />
        </b-tab-item>
        <b-tab-item :label="$t('equipments-view.component-title')" icon="robot-industrial">
          <Equipments :project_id="id" :is_template="isTemplateMode" />
        </b-tab-item>
        <b-tab-item :label="$t('consumables-view.component-title')" icon="cart-variant">
          <Consumables :project_id="id" :is_template="isTemplateMode" />
        </b-tab-item>
        <b-tab-item v-if="!isTemplateMode" :label="$t('files-view.component-title')" icon="text-box-multiple-outline">
          <FilesList :project_id="id" />
        </b-tab-item>
      </b-tabs>
    </OmxCard>

    <Comments :document_id="id" />
  </div>
</template>

<script>
import firebase from "firebase/app";
import Tasks from '@/components/Tasks';
import Personnel from '@/components/Personnel';
import Equipments from '@/components/Equipments';
import Consumables from '@/components/Consumables';
import Comments from "@/components/Comments";
import ImagePicker from "@/components/ImagePicker";
import FilesList from "@/components/FilesList";
import OmxPageHeader from "@/components/OmxPageHeader.vue";
import OmxCard from "@/components/OmxCard.vue";
import OmxField from "@/components/OmxField.vue";
import OmxTagInput from "@/components/OmxTagInput.vue";
import OmxStatTile from "@/components/OmxStatTile.vue";
import OmxSkeleton from "@/components/OmxSkeleton.vue";
import { ls } from "@/utils/storage";

export default {
  name: "ProjectDetails",
  components: {
    OmxPageHeader,
    OmxCard,
    OmxField,
    OmxTagInput,
    OmxStatTile,
    OmxSkeleton,
    FilesList,
    Tasks,
    Personnel,
    Equipments,
    Consumables,
    Comments,
    ImagePicker
  },
  props: ['id'],
  data() {
    return {
      projectData: {
        phase: "initiation"
      },
      isLoading: false,
      siteManagersTags: [],
      projectManagersTags: [],
      projectManagersData: [],
      siteManagersData: [],
      filteredProjectManagers: [],
      filteredSiteManagers: [],
      isFetching: false,
      isAdvancedEditMode: false,
      prevRoute: null,
      activeTab: 0,
      projectPicFile: null,
      projectPicUrl: null,
      summaryStats: null,
      isLoadingStats: false,
      tasksCount: null,
      isLoadingTasks: false
    }
  },
  methods: {
    createProject: function (updateData) {
      this.isLoading = true
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const insertProjectFunction = functions.httpsCallable(this.getSubmitEndpoint());
      updateData["action"] = "insert_project";
      insertProjectFunction(updateData).then(() => {
        this.isLoading = false
        this.showSuccess(this.$t('messages.project-created'));
        this.backClicked()
      }).catch((error) => {
        this.isLoading = false
        this.showError(this.$t('messages.save-error'));
        console.error("Error creating project: ", error?.code);
      });
    },
    updateProject: function (updateData) {
      this.isLoading = true
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const updateProjectFunction = functions.httpsCallable(this.getSubmitEndpoint());
      updateData["action"] = "update_project";
      updateProjectFunction(updateData).then((result) => {
        this.isLoading = false;
        this.exitEditMode();
        this.showSuccess(this.$t('messages.project-updated'));
        if (result.data.payload !== undefined && result.data.payload["projects"].updated[0].is_archived) {
          this.projectData.is_archived = result.data.payload["projects"].updated[0].is_archived["current"]
        }
      }).catch((error) => {
        this.isLoading = false;
        this.showError(this.$t('messages.save-error'));
        console.error("Error updating project: ", error?.code);
      });
      this.updateProjectPicture()
    },
    downloadProjectPicUrl: function () {
      this.projectPicFile = null
      this.projectPicUrl = null
      this.getProjectPicRef().getDownloadURL().then((downloadURL) => {
        this.projectPicUrl = downloadURL
      }).catch((error) => {
        console.log('projectPic error: ', error?.code);
      });
    },
    updateProjectPicture: function () {
      if (this.projectPicFile !== null && this.projectPicFile !== undefined) {
        this.projectPicUrl = ""
        this.getProjectPicRef().put(this.projectPicFile).then(() => {
          this.downloadProjectPicUrl()
        }).catch((error) => {
          this.showError(error.message)
          console.log('Project pic upload error: ', error?.code);
          this.downloadProjectPicUrl()
        });
      } else {
        console.log("No project pic is selected!")
      }
    },
    getProjectPicRef: function () {
      const imagePath = this.getStorageRootFolder() + '/projects/' + this.id + '/projectPic'
      return firebase.storage().ref(imagePath)
    },
    onProjectPicFileSelected: function (selectedImageFile) {
      this.projectPicFile = selectedImageFile
      this.projectPicUrl = URL.createObjectURL(this.projectPicFile)
    },
    onProjectPicFileRemoveSelected() {
      this.downloadProjectPicUrl()
    },
    deleteProject: function () {
      this.isLoading = true;
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const deleteProjectFunction = functions.httpsCallable(this.getSubmitEndpoint());
      deleteProjectFunction({
        action: "delete_project",
        document_id: this.id,
      }).then(() => {
        this.isLoading = false;
        this.showSuccess(this.$t('messages.project-deleted'));
        this.$emit('projectDeleted');
        this.$router.back();
      }).catch((error) => {
        this.isLoading = false;
        this.showError(error.message)
        console.log("Error getting delete result: ", error?.code);
      });
    },
    readSummaryStats: function () {
      this.isLoadingStats = true;
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const fn = functions.httpsCallable(this.getSubmitEndpoint());
      fn({ action: "get_project_summary", document_id: this.id, fill_meta: true })
          .then((result) => {
            const logs = (result.data && result.data.grouped_logs) || [];
            const agg = logs.reduce((a, l) => {
              a.internal += Number(l.total_logged_durations_internal_personnel) || 0;
              a.external += Number(l.total_logged_durations_external_personnel) || 0;
              a.profit   += Number(l.total_profit) || 0;
              return a;
            }, { internal: 0, external: 0, profit: 0 });
            this.summaryStats = agg;
          })
          .catch((error) => {
            console.log("Error get_project_summary: ", error?.code);
            this.summaryStats = { internal: 0, external: 0, profit: 0 };
          })
          .finally(() => { this.isLoadingStats = false; });
    },
    onTasksLoaded(count) {
      // Fed by the Tasks child component after its own get_tasks call, so
      // the stat tile and the tab don't both round-trip the server.
      this.tasksCount = count;
      this.isLoadingTasks = false;
    },
    readProjectData: function () {
      this.isLoading = true
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const getProjectFunction = functions.httpsCallable(this.getSubmitEndpoint());
      getProjectFunction({action: "get_project_by_id", document_id: this.id, fill_meta: true})
          .then((result) => {
            this.isLoading = false
            this.projectData = result.data["projects"];
            this.siteManagersTags = this.projectData.meta["site_managers"]
            this.projectManagersTags = this.projectData.meta["managers"]
          })
          .catch((error) => {
            this.isLoading = false;
            this.showError(error.message)
            if (error.message === "InvalidPath") {
              this.$router.replace("../projects");
            }
            console.log("Error getting project: ", error?.code);
          });
    },
    readUsersData: function () {
      this.isFetching = true
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const getUsersFunction = functions.httpsCallable(this.getSubmitEndpoint());
      getUsersFunction({action: "get_users"})
          .then((result) => {
            this.isFetching = false
            // all users except inactive ones
            const allActiveUsersData = result.data["users"].filter(function (el) {
              return el.roles.includes("inactive") !== true;
            });
            // active users who are admin or project_manager can be a project manager
            this.projectManagersData = allActiveUsersData.filter(function (el) {
              return el.roles.includes("site_manager") !== true;
            });
            // every active user could be a site manager
            this.siteManagersData = allActiveUsersData
            this.filteredProjectManagers = this.projectManagersData
            this.filteredSiteManagers = this.siteManagersData
            // setting the current user as project manager when creating a new project
            if (this.isInNewMode) {
              const currentUser = JSON.parse(ls.get("currentUser"))
              this.projectManagersTags = allActiveUsersData.filter(function (el) {
                return el.email === currentUser.email;
              });
            }
          })
          .catch((error) => {
            this.isFetching = false
            this.showError(error.message)
            console.log("Error getting users: ", error?.code);
          });
    },
    getFilteredProjectManagers(text) {
      this.filteredProjectManagers = this.projectManagersData.filter((option) => {
        return option.email
            .toString()
            .toLowerCase()
            .indexOf(text.toLowerCase()) >= 0
      })
    },
    getFilteredSiteManagers(text) {
      this.filteredSiteManagers = this.siteManagersData.filter((option) => {
        return option.email
            .toString()
            .toLowerCase()
            .indexOf(text.toLowerCase()) >= 0
      })
    },
    archiveProject() {
      const dataFieldsToUpdate = {
        document_id: this.id,
        is_archived: true
      }
      this.updateProject(dataFieldsToUpdate);
    },
    onProjectManagersInput() {
      this.filteredProjectManagers = this.projectManagersData
    },
    onSiteManagersInput() {
      this.filteredSiteManagers = this.siteManagersData
    },
    /*
     UI Events
     */
    editClicked: function () {
      // Edit-as-route: navigate to /projects/:id/edit. isEditMode is a
      // computed derived from $route.name, so the UI updates reactively.
      this.$router.push({ name: 'ProjectEdit', params: { id: this.id } })
          .catch(() => { /* navigation duplicated */ });
    },
    exitEditMode: function () {
      // Return from edit route to view route. No-op for new mode (handled
      // by createProject → backClicked).
      if (this.isInNewMode) return;
      if (this.$route.name === 'ProjectEdit') {
        this.$router.replace({ name: 'ProjectDetails', params: { id: this.id } })
            .catch(() => { /* navigation duplicated */ });
      }
    },
    async deleteClicked() {
      if (this.isTemplateMode) {
        const ok = await this.$omxConfirm({
          title: this.$t('templates-view.delete-template-title').toString(),
          message: this.$t('templates-view.delete-template-message').toString(),
          confirmText: this.$t('templates-view.delete-template-button').toString(),
          cancelText: this.$t('common.cancel').toString(),
          type: 'is-danger',
          icon: 'delete'
        });
        if (ok) this.deleteProject();
        return;
      }
      if (!this.projectData.is_archived) {
        await this.$omxAlert({
          title: this.$t('project-details-view.delete-confirm-title').toString(),
          message: this.$t('project-details-view.delete-unarchived-project-message').toString(),
          type: 'is-warning',
          icon: 'alert'
        });
        return;
      }
      const ok = await this.$omxConfirm({
        title: this.$t('project-details-view.delete-confirm-title').toString(),
        message: this.$t('project-details-view.delete-confirm-message').toString(),
        confirmText: this.$t('project-details-view.delete-confirm-button').toString(),
        cancelText: this.$t('common.cancel').toString(),
        type: 'is-danger',
        icon: 'delete'
      });
      if (ok) this.deleteProject();
    },
    async advancedEditClicked() {
      const ok = await this.$omxConfirm({
        title: this.$t('project-details-view.advanced-edit-confirm-title').toString(),
        message: this.$t('project-details-view.advanced-edit-confirm-message').toString(),
        confirmText: this.$t('project-details-view.advanced-edit-confirm-button').toString(),
        cancelText: this.$t('common.cancel').toString(),
        type: 'is-warning',
        icon: 'alert'
      });
      if (ok) this.isAdvancedEditMode = true;
    },
    cancelClicked: function () {
      if (this.isInNewMode) {
        this.$router.back();
        return;
      }
      if (this.isEditMode) {
        this.readProjectData();
        this.downloadProjectPicUrl();
      }
      this.isAdvancedEditMode = false;
      this.exitEditMode();
    },
    saveClicked: function () {
      // Required fields must be present before we attempt to save.
      const code = (this.projectData.code || "").toString().trim();
      const title = (this.projectData.title || "").toString().trim();
      if (!code || !title) {
        this.showError(this.$t('messages.required-fields-error'));
        return;
      }

      const dataFieldsToUpdate = {
        code: this.projectData.code,
        title: this.projectData.title,
        description: this.projectData.description,
        phase: (this.projectData.phase === null) ? this.projectPhaseOptions[0].id : this.projectData.phase,
        site_manager_ids: this.siteManagersTags.map(user => user.document_id),
        manager_ids: this.projectManagersTags.map(user => user.document_id),
        internal_personal_cost: (this.projectData.internal_personal_cost === "") ? 0 : parseInt(this.projectData.internal_personal_cost),
        internal_personal_day_in_hours: (this.projectData.internal_personal_day_in_hours === "") ? 0 : parseInt(this.projectData.internal_personal_day_in_hours),
        external_personal_cost: (this.projectData.external_personal_cost === "") ? 0 : parseInt(this.projectData.external_personal_cost),
        external_personal_day_in_hours: (this.projectData.external_personal_day_in_hours === "") ? 0 : parseInt(this.projectData.external_personal_day_in_hours)
      }

      if (this.isInNewMode) {
        this.createProject(dataFieldsToUpdate);
      } else {
        dataFieldsToUpdate["document_id"] = this.id
        this.updateProject(dataFieldsToUpdate);
      }
      this.isAdvancedEditMode = false;
    },
    worklogClicked: function () {
      this.$router.push({
        name: 'ProjectLogs',
        params: {
          project_id: this.id
        }
      })
    },
    summaryClicked: function () {
      this.$router.push({
        name: 'ProjectSummary',
        params: {
          project_id: this.id
        }
      })
    },
    exportClicked: function () {
      this.isLoading = true
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const exportProjectFunction = functions.httpsCallable(this.getSubmitEndpoint());
      exportProjectFunction({action: "export_project", document_id: this.id})
          .then((result) => {
            this.isLoading = false
            if (result.data.url !== null && result.data.url !== "" && result.data.url !== undefined) {
              this.$omxAlert({
                title: this.$t('project-details-view.export-ready-title').toString(),
                message: result.data.url,
                type: 'is-info',
                confirmText: this.$t('project-details-view.export-ready-done-button').toString()
              });
            }
          })
          .catch((error) => {
            this.isLoading = false
            this.showError(error.message)
            console.log("Error exporting project: ", error?.code);
          });
    },
    async saveAsTemplateClicked() {
      const r = await this.$omxPrompt({
        message: this.$t('project-details-view.save-as-template-title').toString(),
        inputAttrs: { type: 'text', placeholder: this.$t('project-details-view.template-code-placeholder').toString() },
        confirmText: this.$t('common.save').toString(),
        closeOnConfirm: false
      });
      if (r === null) return;
      this.$buefy.toast.open(this.$t('project-details-view.saving-template-title').toString())
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const saveAsTemplateFunction = functions.httpsCallable(this.getSubmitEndpoint());
      const payload = {action: "copy_project", document_id: this.id, code: r.value, save_as: "template"};
      saveAsTemplateFunction(payload)
          .then(() => {
            this.showSuccess(this.$t('project-details-view.save-template-success'))
            if (r.close) r.close();
          })
          .catch((error) => {
            console.log("Error saving template: ", error?.code);
            this.showError(error.message);
            if (r.close) r.close();
          });
    },
    async saveAsProjectClicked() {
      const r = await this.$omxPrompt({
        message: this.$t('templates-view.save-as-project-title').toString(),
        inputAttrs: { type: 'text', placeholder: this.$t('templates-view.project-code-placeholder').toString() },
        confirmText: this.$t('common.save').toString(),
        closeOnConfirm: false
      });
      if (r === null) return;
      this.isLoading = true
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const saveAsProjectFunction = functions.httpsCallable(this.getSubmitEndpoint());
      const payload = {action: "copy_project", document_id: this.id, code: r.value, save_as: "project"};
      saveAsProjectFunction(payload)
          .then((result) => {
            this.isLoading = false;
            if (r.close) r.close();
            this.showSuccess(this.$t('templates-view.save-project-success'));
            const insertedProjectId = result.data.payload["projects"].inserted[0].document_id;
            if (insertedProjectId !== undefined) {
              this.$router.replace({
                name: 'ProjectDetails',
                params: { id: insertedProjectId }
              }).catch(() => {});
            }
          })
          .catch((error) => {
            this.isLoading = false;
            this.showError(error.message);
            if (r.close) r.close();
            console.log("Error saving project: ", error?.code);
          });
    },
    async archiveClicked() {
      const ok = await this.$omxConfirm({
        title: this.$t('project-details-view.archive-project-confirm-title').toString(),
        message: this.$t('project-details-view.archive-project-confirm-message').toString(),
        confirmText: this.$t('project-details-view.archive-project-confirm-button').toString(),
        cancelText: this.$t('common.cancel').toString(),
        type: 'is-danger',
        icon: 'archive-arrow-down'
      });
      if (ok) this.archiveProject();
    },
    unArchiveClicked: function () {
      const dataFieldsToUpdate = {
        document_id: this.id,
        is_archived: false
      }
      this.updateProject(dataFieldsToUpdate);
    },
    backClicked: function () {
      this.$router.back()
    },
    phaseTone(phase) {
      // Map project phases to header-pill tones.
      switch (phase) {
        case 'execution':  return 'live';
        case 'monitoring': return 'live';
        case 'planning':   return 'draft';
        case 'initiation': return 'draft';
        case 'closure':    return 'neutral';
        default:           return 'neutral';
      }
    }
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.prevRoute = from
    })
  },
  mounted() {
    if ((!this.isTemplateMode && !this.currentUserAccess("ProjectDetails", this.UserActions.VIEW, this.id))
        || (this.isTemplateMode
            && !this.currentUserAccess("ProjectTemplates", this.UserActions.VIEW))) {
      console.log("Page access denied.");
      this.$router.replace("/projects/").catch(() => {
      });
    }
    if (this.prevRoute != null) {
      if (this.prevRoute.path.includes("personnel")) {
        this.activeTab = 1;
      } else if (this.prevRoute.path.includes("equipments")) {
        this.activeTab = 2;
      } else if (this.prevRoute.path.includes("consumables")) {
        this.activeTab = 3;
      }
    }
    this.readUsersData();
    if (this.isInNewMode) {
      // isEditMode is a computed (route-derived) — no need to flip it.
      console.log("creating new project");
    } else {
      this.readProjectData();
      this.downloadProjectPicUrl();
      this.readSummaryStats();
      // tasksCount is fed by the Tasks child via @tasks-loaded; keep
      // isLoadingTasks true until that event arrives.
      this.isLoadingTasks = true;
    }
  },
  computed: {
    isInNewMode() {
      return this.id === "new";
    },
    isEditMode() {
      // Edit-as-route: /projects/:id/edit drives edit UI. New mode is
      // always editable. Replaces the old toggle-based flag.
      return this.$route.name === 'ProjectEdit' || this.isInNewMode;
    },
    headerTitle() {
      if (this.isInNewMode) return this.$t('project-details-view.new-project-title');
      return this.getAdjustedTitle(this.projectData.title);
    },
    headerInitials() {
      if (this.isInNewMode) return null;
      const code = this.projectData.code || "";
      const codeLetters = (code.split("-")[0] || "").trim();
      if (codeLetters) return codeLetters.slice(0, 3).toUpperCase();
      const title = this.projectData.title || "";
      return title.slice(0, 2).toUpperCase() || null;
    },
    headerSubtitle() {
      const parts = [];
      if (this.projectData.code) parts.push(this.projectData.code);
      if (this.isTemplateMode) parts.push(this.$t('project-details-view.view-template-title'));
      return parts.join(' · ');
    },
    headerPills() {
      const pills = [];
      // Phase pill — semantic tone based on phase
      if (this.projectData.phase) {
        const phaseOpt = this.projectPhaseOptions.find(o => o.id === this.projectData.phase);
        const label = phaseOpt ? this.$t(phaseOpt.translationKey) : this.projectData.phase;
        const tone = this.phaseTone(this.projectData.phase);
        pills.push({ label, tone });
      }
      if (this.isProjectArchived) {
        pills.push({ label: this.$t('projects-view.archived-column'), tone: 'warn' });
      }
      if (this.isTemplateMode) {
        pills.push({ label: this.$t('project-details-view.view-template-title'), tone: 'draft', dot: false });
      }
      return pills;
    },
    canEdit() {
      return (!this.isTemplateMode && this.currentUserAccess('Projects', 'UPDATE', this.id))
          || (this.isTemplateMode && this.currentUserAccess('ProjectTemplates', 'UPDATE'));
    },
    canSave() {
      return (!this.isTemplateMode && (
              (this.isInNewMode && this.currentUserAccess('ProjectDetails', 'CREATE', this.id))
              || this.currentUserAccess('ProjectDetails', 'UPDATE', this.id)
          ))
          || (this.isTemplateMode && this.currentUserAccess('ProjectTemplates', 'UPDATE'));
    },
    canDelete() {
      return (!this.isTemplateMode && this.currentUserAccess('Projects', 'DELETE', this.id))
          || (this.isTemplateMode && this.currentUserAccess('ProjectTemplates', 'DELETE'));
    },
    canArchive() {
      return !this.isLoading && !this.isTemplateMode && !this.isInNewMode
          && !this.isProjectArchived && this.isEditMode
          && this.currentUserAccess('ProjectDetails', 'DELETE', this.id);
    },
    canUnarchive() {
      return !this.isLoading && !this.isTemplateMode && !this.isInNewMode
          && this.isProjectArchived && this.isEditMode
          && this.currentUserAccess('ProjectDetails', 'DELETE');
    },
    projectPhaseFieldOptions() {
      return (this.projectPhaseOptions || []).map(opt => ({
        value: opt.id,
        label: this.$t(opt.translationKey)
      }));
    },
    pageTitle() {
      if (this.isInNewMode) {
        return this.$t('project-details-view.new-project-title')
      } else if (this.isEditMode) {
        return (this.isTemplateMode ? this.$t('project-details-view.edit-template-title') : this.$t('project-details-view.edit-project-title')) + " " + this.projectData.code
      } else {
        return (this.isTemplateMode ? this.$t('project-details-view.view-template-title') : this.$t('project-details-view.view-project-title')) + " " + this.projectData.code
      }
    },
    isProjectArchived() {
      return this.projectData.is_archived
    },
    projectStatus() {
      if (!this.isEditMode) {
        const st = this.projectPhaseOptions.find(obj => {
          return obj.id === this.projectData.phase
        });
        if (st !== undefined)
          return this.$t(st.translationKey);
      }
      return "";
    },
    isTemplateMode() {
      return this.projectData.is_template
    },
    showStats() {
      return !this.isInNewMode && !this.isTemplateMode;
    },
    statHasLogs() {
      const s = this.summaryStats;
      return !!s && (s.internal + s.external + Math.abs(s.profit)) > 0;
    },
    statHoursValue() {
      if (!this.summaryStats) return "–";
      const total = this.summaryStats.internal + this.summaryStats.external;
      return this.getFormattedTimeHour(total);
    },
    statHoursDelta() {
      if (!this.statHasLogs) return this.$t('project-details-view.stat-no-logs');
      const ext = this.getFormattedTimeHour(this.summaryStats.external);
      const int = this.getFormattedTimeHour(this.summaryStats.internal);
      return int + " · " + ext;
    },
    statProfitValue() {
      if (!this.summaryStats) return "–";
      return this.getFormattedPricing(this.toFixedIfNecessary(this.summaryStats.profit));
    },
    statProfitTone() {
      if (!this.summaryStats) return 'green';
      return this.summaryStats.profit >= 0 ? 'green' : 'warm';
    },
    statProfitDirection() {
      if (!this.summaryStats) return 'flat';
      if (this.summaryStats.profit > 0) return 'up';
      if (this.summaryStats.profit < 0) return 'down';
      return 'flat';
    },
    statExternalValue() {
      if (!this.summaryStats) return "–";
      const total = this.summaryStats.internal + this.summaryStats.external;
      if (total <= 0) return "0%";
      const share = (this.summaryStats.external / total) * 100;
      return this.toFixedIfNecessary(share, 1) + "%";
    },
    statExternalDelta() {
      if (!this.statHasLogs) return null;
      const share = this.getFormattedTimeHour(this.summaryStats.external);
      return this.$t('project-details-view.stat-external-delta-target', { share });
    },
    statTasksValue() {
      if (this.tasksCount === null) return "–";
      return String(this.tasksCount);
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/tokens";

.project-details {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.project-details__template-msg {
  margin-bottom: 0;
}
.project-details__stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
}
@media (max-width: 900px) {
  .project-details__stats { grid-template-columns: repeat(2, 1fr); }
}
.project-details__card {
  // Internal form sections still use Buefy columns; preserve their layout
  // while sitting on top of the new card surface.
  ::v-deep .columns:last-child { margin-bottom: 0; }
  ::v-deep .notification {
    background: $omx-ink-50;
    border: 1px solid $omx-ink-100;
    border-radius: $omx-radius-md;
    padding: 16px;
  }
}
.project-details__tabs-card {
  padding: 0 !important;
  overflow: hidden;
  ::v-deep .b-tabs .tabs ul {
    border-bottom: 1px solid $omx-ink-100;
  }
  ::v-deep .b-tabs .tab-content { padding: 18px; }
}
</style>
